/** tm/components/GovFeedPdfView.tsx — 공고문 PDF 뷰어 (pdf.js · 2026-09-30 계약 변경: PDF 안에서 형광·검색·선택)
 *
 *  · 페이지를 캔버스로 그리고 그 위에 pdf.js 글 층(TextLayer)을 올린다 — 글 선택·형광이 PDF 위에서 된다.
 *  · 폭 맞춤 × zoom, 보이는 페이지(±800px)만 그린다(가상화). 모든 페이지의 글은 처음에 한 번 뽑아
 *    «이어 붙인 글»과 «글 조각(span) 위치표»를 만든다 → 검색·근거 인용은 이 글에서 찾고, 위치표로 span 에 형광을 칠한다.
 *  · 바이트는 헤더 인증으로 받는다(/gov-feed/doc/:itemId/:key — 운영·개발 같은 방식).
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { C, T } from './ui';
import * as api from '../api';

type PdfjsLib = typeof import('pdfjs-dist');
type PdfDoc = import('pdfjs-dist').PDFDocumentProxy;
type TextContent = Awaited<ReturnType<import('pdfjs-dist').PDFPageProxy['getTextContent']>>;

let libP: Promise<PdfjsLib> | null = null;
/** pdf.js 를 한 번만 불러온다 — 워커는 PdfEditorPage 와 같이 cdnjs 에서 (버전 일치) */
function pdfjs(): Promise<PdfjsLib> {
  if (!libP) {
    libP = import('pdfjs-dist').then(lib => {
      lib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${lib.version}/pdf.worker.min.mjs`;
      return lib;
    });
    libP.catch(() => { libP = null; });
  }
  return libP;
}

export interface PdfModel {
  doc: PdfDoc;
  pages: { w: number; h: number; content: TextContent; starts: number[]; ends: number[] }[];
  /** 모든 페이지 글을 이어 붙인 것 (글 조각 사이 줄바꿈 = hasEOL, 페이지 사이 빈 줄) */
  text: string;
}

const modelCache = new Map<string, Promise<PdfModel>>();
/** PDF 받기 + 글 뽑기 (주소별 캐시, 최근 6개) */
export function loadPdfModel(viewUrl: string): Promise<PdfModel> {
  const hit = modelCache.get(viewUrl);
  if (hit) return hit;
  const p = (async () => {
    const [lib, buf] = await Promise.all([pdfjs(), api.govFeedDocBytes(viewUrl)]);
    const doc = await lib.getDocument({ data: new Uint8Array(buf) }).promise;
    const pages: PdfModel['pages'] = [];
    let text = '';
    for (let i = 1; i <= doc.numPages; i++) {
      const page = await doc.getPage(i);
      const vp = page.getViewport({ scale: 1 });
      const content = await page.getTextContent();
      const starts: number[] = []; const ends: number[] = [];
      for (const it of content.items) {
        const s = 'str' in it ? it.str : '';
        starts.push(text.length);
        text += s;
        ends.push(text.length);
        if ('hasEOL' in it && it.hasEOL) text += '\n';
      }
      text += '\n\n';
      pages.push({ w: vp.width, h: vp.height, content, starts, ends });
    }
    return { doc, pages, text };
  })();
  modelCache.set(viewUrl, p);
  p.catch(() => modelCache.delete(viewUrl));
  if (modelCache.size > 6) { const first = modelCache.keys().next().value; if (first && first !== viewUrl) modelCache.delete(first); }
  return p;
}

/** 바깥 화면은 그대로 두고 가장 가까운 스크롤 칸만 움직여 el 을 가운데로 (scrollIntoView 는 앱 전체를 밀어 올린다) */
export function scrollToCenter(el: HTMLElement, smooth = false) {
  let box = el.parentElement;
  while (box && !(box.scrollHeight > box.clientHeight && /(auto|scroll)/.test(getComputedStyle(box).overflowY))) box = box.parentElement;
  if (!box) return;
  const r = el.getBoundingClientRect(); const b = box.getBoundingClientRect();
  const top = box.scrollTop + (r.top - b.top) - (b.height - r.height) / 2;
  box.scrollTo({ top: Math.max(0, top), behavior: smooth ? 'smooth' : 'auto' });
}

export interface PdfMark { start: number; end: number; k: 'q' | 'h' | 'ha' }

const MARK_BG: Record<PdfMark['k'], string> = { q: 'rgba(250, 204, 21, 0.45)', h: 'rgba(253, 224, 71, 0.35)', ha: 'rgba(249, 115, 22, 0.45)' };

const STYLE = `
.gfp-page{position:relative;margin:0 auto 12px;background:#fff;box-shadow:0 1px 4px rgba(0,0,0,0.25)}
.gfp-page canvas{position:absolute;inset:0;width:100%;height:100%}
.gfp-tl{position:absolute;inset:0;overflow:clip;line-height:1;text-align:initial;transform-origin:0 0;z-index:0;-webkit-text-size-adjust:none;text-size-adjust:none;forced-color-adjust:none}
.gfp-tl :is(span,br){color:transparent;position:absolute;white-space:pre;cursor:text;transform-origin:0% 0%}
.gfp-tl ::selection{background:rgba(37,99,235,0.28)}
.gfp-tl mark{color:transparent;border-radius:2px;padding:1px 0;margin:-1px 0}
.gfp-tl .endOfContent{display:block;position:absolute;inset:100% 0 0;z-index:0;cursor:default;user-select:none}
`;

interface PageProps {
  model: PdfModel; lib: PdfjsLib; index: number; scale: number; visible: boolean;
  marks: PdfMark[]; scrollMark: { start: number; nonce: number } | null;
}

/** 한 페이지 — 보일 때만 캔버스 + 글 층 */
function PdfPage({ model, lib, index, scale, visible, marks, scrollMark }: PageProps) {
  const pg = model.pages[index];
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tlRef = useRef<HTMLDivElement>(null);
  const [divs, setDivs] = useState<HTMLElement[] | null>(null);

  useEffect(() => {
    if (!visible) { setDivs(null); return; }
    let cancelled = false;
    let task: { cancel: () => void } | null = null;
    let tl: { cancel: () => void } | null = null;
    (async () => {
      const page = await model.doc.getPage(index + 1);
      if (cancelled || !canvasRef.current || !tlRef.current) return;
      const vp = page.getViewport({ scale });
      const ratio = Math.min(2, window.devicePixelRatio || 1);
      const cv = canvasRef.current;
      cv.width = Math.floor(vp.width * ratio); cv.height = Math.floor(vp.height * ratio);
      const ctx = cv.getContext('2d');
      if (!ctx) return;
      const rt = page.render({ canvasContext: ctx, canvas: cv, viewport: vp, transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : undefined } as Parameters<typeof page.render>[0]);
      task = rt;
      const box = tlRef.current;
      box.replaceChildren();
      const layer = new lib.TextLayer({ textContentSource: pg.content, container: box, viewport: vp });
      tl = layer;
      await layer.render();
      if (cancelled) return;
      const end = document.createElement('div'); end.className = 'endOfContent'; box.append(end);
      setDivs(layer.textDivs.slice());
      await rt.promise.catch(() => {});
    })().catch(() => {});
    return () => { cancelled = true; task?.cancel(); tl?.cancel(); };
  }, [visible, scale, model, lib, index, pg]);

  // 형광 — 이 페이지 글 조각에 겹치는 표시만 칠한다
  useLayoutEffect(() => {
    if (!divs) return;
    const strs = pg.content.items.map(it => ('str' in it ? it.str : ''));
    const pStart = pg.starts[0] ?? 0; const pEnd = pg.ends[pg.ends.length - 1] ?? 0;
    const mine = marks.filter(m => m.end > pStart && m.start < pEnd);
    let target: HTMLElement | null = null;
    divs.forEach((div, i) => {
      const s0 = pg.starts[i]; const e0 = pg.ends[i]; const str = strs[i] ?? '';
      const hits = mine.filter(m => m.end > s0 && m.start < e0).sort((a, b) => a.start - b.start);
      if (!hits.length) { if (div.childElementCount) div.textContent = str; return; }
      const frag = document.createDocumentFragment();
      let pos = 0;
      for (const m of hits) {
        const a = Math.max(0, m.start - s0); const b = Math.min(str.length, m.end - s0);
        if (a < pos) continue;
        if (a > pos) frag.append(str.slice(pos, a));
        const mk = document.createElement('mark');
        mk.style.background = MARK_BG[m.k];
        mk.textContent = str.slice(a, b);
        if (scrollMark && m.start === scrollMark.start && !target) target = mk;
        frag.append(mk);
        pos = b;
      }
      if (pos < str.length) frag.append(str.slice(pos));
      div.replaceChildren(frag);
    });
    const t = target as HTMLElement | null;
    if (t) scrollToCenter(t);
    // scrollMark.nonce 로 같은 표시를 다시 눌러도 스크롤
  }, [divs, marks, pg, scrollMark]);

  return (
    <div className="gfp-page" data-page={index} style={{ width: pg.w * scale, height: pg.h * scale }}>
      <canvas ref={canvasRef} />
      <div ref={tlRef} className="gfp-tl" style={{ ['--total-scale-factor' as string]: String(scale), ['--scale-round-x' as string]: '1px', ['--scale-round-y' as string]: '1px' }} />
      {!visible && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, fontSize: T.xs }}>{index + 1}쪽</div>}
    </div>
  );
}

interface Props {
  viewUrl: string;
  zoom: number;
  marks: PdfMark[];
  /** 이 위치의 표시로 스크롤 (nonce 가 바뀔 때마다) */
  scrollMark: { start: number; nonce: number } | null;
  onModel: (m: PdfModel) => void;
  onMouseUp: () => void;
  onPageInfo?: (cur: number, total: number) => void;
  boxRef?: React.RefObject<HTMLDivElement>;
  /** pdf.js 로 못 열면 알린다 (부모가 서버가 뽑은 글로 대신 보여 준다) */
  onError?: (msg: string) => void;
  /** 여는 중 문구 (HWP→PDF 변환 중 등) */
  loadingLabel?: string;
}

export default function GovFeedPdfView({ viewUrl, zoom, marks, scrollMark, onModel, onMouseUp, onPageInfo, boxRef, onError, loadingLabel }: Props) {
  const [model, setModel] = useState<PdfModel | null>(null);
  const [lib, setLib] = useState<PdfjsLib | null>(null);
  const [err, setErr] = useState('');
  const ownRef = useRef<HTMLDivElement>(null);
  const scrollRef = boxRef || ownRef;
  const [width, setWidth] = useState(0);
  const [visible, setVisible] = useState<Set<number>>(new Set([0]));
  const onModelRef = useRef(onModel);
  onModelRef.current = onModel;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    let cancelled = false;
    setModel(null); setErr('');
    Promise.all([pdfjs(), loadPdfModel(viewUrl)])
      .then(([l, m]) => { if (cancelled) return; setLib(l); setModel(m); onModelRef.current(m); })
      .catch(e => { if (!cancelled) { const m = e instanceof Error ? e.message : 'PDF를 열 수 없습니다'; setErr(m); onErrorRef.current?.(m); } });
    return () => { cancelled = true; };
  }, [viewUrl]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, [scrollRef, model]);

  const maxW = useMemo(() => (model ? Math.max(...model.pages.map(p => p.w)) : 600), [model]);
  const scale = Math.max(0.3, ((width || 600) - 28) / maxW) * zoom;

  // 보이는 페이지 (±800px)
  useEffect(() => {
    const root = scrollRef.current;
    if (!root || !model) return;
    const io = new IntersectionObserver(es => {
      setVisible(prev => {
        const nx = new Set(prev);
        es.forEach(e => { const i = Number((e.target as HTMLElement).dataset.page); if (e.isIntersecting) nx.add(i); else nx.delete(i); });
        return nx;
      });
    }, { root, rootMargin: '800px 0px' });
    root.querySelectorAll('.gfp-page').forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [model, scale, scrollRef]);

  // 형광 위치로 — 그 페이지가 아직 안 그려졌으면 페이지로 먼저 스크롤 (그리면 PdfPage 가 형광으로 맞춘다)
  useEffect(() => {
    if (!scrollMark || !model || !scrollRef.current) return;
    const pi = model.pages.findIndex(p => (p.ends[p.ends.length - 1] ?? -1) >= scrollMark.start);
    if (pi < 0) return;
    const el = scrollRef.current.querySelector(`.gfp-page[data-page="${pi}"]`) as HTMLElement | null;
    if (el && !visible.has(pi)) scrollRef.current.scrollTop = el.offsetTop - 40;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollMark?.nonce, model]);

  // 지금 페이지 (머리 표시용)
  const onScroll = () => {
    const root = scrollRef.current;
    if (!root || !model || !onPageInfo) return;
    const pages = Array.from(root.querySelectorAll('.gfp-page')) as HTMLElement[];
    const mid = root.scrollTop + root.clientHeight / 3;
    const cur = pages.findIndex(p => p.offsetTop + p.offsetHeight > mid);
    onPageInfo(Math.max(0, cur) + 1, model.pages.length);
  };
  useEffect(() => { if (model && onPageInfo) onPageInfo(1, model.pages.length); }, [model, onPageInfo]);

  return (
    <div ref={scrollRef} onScroll={onScroll} onMouseUp={onMouseUp}
      style={{ position: 'absolute', inset: 0, overflow: 'auto', background: '#E5E7EB', padding: '12px 14px', boxSizing: 'border-box' }}>
      <style>{STYLE}</style>
      {err ? <div style={{ padding: 24, textAlign: 'center', color: C.red, fontSize: T.sm }}>{err}</div>
        : !model || !lib ? <div style={{ padding: 24, textAlign: 'center', color: C.sub, fontSize: T.sm }}>{loadingLabel || '공고문 PDF 여는 중…'}</div>
          : model.pages.map((_, i) => (
            <PdfPage key={i} model={model} lib={lib} index={i} scale={scale} visible={visible.has(i)} marks={marks} scrollMark={scrollMark} />
          ))}
    </div>
  );
}
